import React, { useState, useEffect } from 'react';
import { authService } from '../../../services/api';

const UserActivity = ({ onNavigate }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUserActivities();
  }, []);

  const loadUserActivities = async () => {
    setLoading(true);
    try {
      const activitiesData = await authService.getUserActions();
      // Ensure activities is always an array
      setActivities(Array.isArray(activitiesData) ? activitiesData : []);
    } catch (error) {
      setError(error.message);
      setActivities([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getActivityIcon = (activityType) => {
    const icons = {
      'login': '🔐',
      'logout': '🚪',
      'post_created': '📝',
      'post_updated': '✏️',
      'post_deleted': '🗑️',
      'comment_created': '💬',
      'like_post': '❤️',
      'share_post': '📤',
      'follow_user': '👥',
      'unfollow_user': '👤',
      'profile_updated': '👤',
      'password_changed': '🔑',
      'email_verified': '📧',
      'phone_verified': '📱',
      'role_changed': '👑',
      'account_locked': '🔒',
      'account_unlocked': '🔓'
    };
    return icons[activityType] || '📊';
  };

  const getActivityColor = (activityType) => {
    const colors = {
      'login': 'text-green-600',
      'logout': 'text-gray-600',
      'post_created': 'text-blue-600',
      'post_updated': 'text-blue-600',
      'post_deleted': 'text-red-600',
      'comment_created': 'text-purple-600',
      'like_post': 'text-red-600',
      'share_post': 'text-blue-600',
      'follow_user': 'text-green-600',
      'unfollow_user': 'text-gray-600',
      'profile_updated': 'text-blue-600',
      'password_changed': 'text-orange-600',
      'email_verified': 'text-green-600',
      'phone_verified': 'text-green-600',
      'role_changed': 'text-purple-600',
      'account_locked': 'text-red-600',
      'account_unlocked': 'text-green-600'
    };
    return colors[activityType] || 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading your activity...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Activity Log</h2>
          <button
            onClick={loadUserActivities}
            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-200 transition-colors"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        {activities.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 text-6xl mb-4">📊</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Activity Yet</h3>
            <p className="text-gray-500">
              Your activity will appear here as you use the platform.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {activities && activities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-lg">{getActivityIcon(activity.activity_type)}</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className={`text-sm font-medium ${getActivityColor(activity.activity_type)}`}>
                      {activity.get_activity_type_display || activity.activity_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(activity.created_at)}
                    </p>
                  </div>
                  {activity.description && (
                    <p className="text-sm text-gray-600 mt-1">
                      {activity.description}
                    </p>
                  )}
                  {activity.ip_address && (
                    <p className="text-xs text-gray-400 mt-1">
                      IP: {activity.ip_address}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {activities.length > 0 && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Showing {activities.length} recent activities
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserActivity;
